import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Box, Button, Drawer, Tooltip } from '@mui/material';
import { FullscreenExit } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import {
  clampNumber,
  currentViewport,
  defaultFloatingRect,
  getDockSizeLimits,
  SIDEBAR_RAIL_WIDTH,
  SIDEBAR_WIDTH,
  useWorkspaceLayout,
} from '../../context/WorkspaceLayoutContext';
import { SplitHandle } from './SplitPane';
import { useFloatingWindow } from './FloatingWindow';

/**
 * In-flow app shell: sidebar | (topbar + main) | Inky dock.
 *
 * The dock element sits at the same position in the React tree for every presentation (pushed
 * right/left/bottom, floating, or a narrow-screen overlay) and is never unmounted, so Inky keeps
 * its conversation, draft and running turn across route changes, dock moves and close/open.
 * `main` is the page scroller; pages stick to its top (`--q-page-sticky-top: 0px`).
 */

const EASE = 'cubic-bezier(0.2, 0, 0, 1)';
const DOCK_TRANSITION_MS = 220;

/** What the dock needs to know about its frame (variant, floating drag handle, close). */
const DockFrameContext = createContext({ variant: 'push', side: 'right', open: true, dragHandleProps: {}, close: () => {} });

/**
 * Dock frame info for the dock's own chrome (title bar drag handle, close button placement).
 * @returns {{variant: 'push'|'floating'|'overlay', side: string, open: boolean, dragHandleProps: object, close: () => void}} Frame.
 */
export const useDockFrame = () => useContext(DockFrameContext);

/** External store with the main scroller's element and width (no React re-render per resize). */
function createMainStore() {
  let width = 0;
  let element = null;
  const listeners = new Set();
  return {
    getWidth: () => width,
    getElement: () => element,
    setElement: (el) => {
      element = el;
    },
    setWidth: (next) => {
      if (next === width) return;
      width = next;
      listeners.forEach((listener) => listener());
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

const MainStoreContext = createContext(null);

/**
 * Whether the shell's main content area is at least `px` wide. Re-renders only when the answer
 * flips. Outside the shell it falls back to the window width.
 * @param {number} px - Threshold.
 * @returns {boolean} True when wide enough.
 */
export function useMainWidthAtLeast(px) {
  const store = useContext(MainStoreContext);
  const subscribe = useCallback((listener) => {
    if (store) return store.subscribe(listener);
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, [store]);
  const getSnapshot = () => (store ? store.getWidth() || currentViewport().width : currentViewport().width) >= px;
  return useSyncExternalStore(subscribe, getSnapshot, () => true);
}

/**
 * The shell's main scroller element (or null outside the shell).
 * @returns {() => HTMLElement|null} Getter.
 */
export function useMainElementGetter() {
  const store = useContext(MainStoreContext);
  return useCallback(() => (store ? store.getElement() : null), [store]);
}

/**
 * Track the viewport size (rAF-throttled) so dock limits follow window resizes.
 * @returns {{width: number, height: number}} Viewport.
 */
function useViewport() {
  const [viewport, setViewport] = useState(currentViewport);
  useEffect(() => {
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const next = currentViewport();
        setViewport((prev) => (prev.width === next.width && prev.height === next.height ? prev : next));
      });
    };
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, []);
  return viewport;
}

/**
 * Scroll the main area to an in-page anchor, retrying briefly while the page renders.
 * @param {HTMLElement} main - Scroller.
 * @param {string} id - Element id.
 * @returns {() => void} Cancel.
 */
function scrollToAnchorInMain(main, id) {
  let tries = 0;
  let raf = 0;
  const attempt = () => {
    const target = document.getElementById(id);
    if (target && main.contains(target)) {
      target.scrollIntoView({ block: 'start', behavior: 'smooth' });
      const focusable = target.matches('input, textarea, button, [tabindex]') ? target : target.querySelector('input, textarea');
      focusable?.focus?.({ preventScroll: true });
      return;
    }
    tries += 1;
    if (tries < 30) raf = requestAnimationFrame(attempt);
  };
  attempt();
  return () => cancelAnimationFrame(raf);
}

/**
 * Where keyboard focus goes when the dock closes while it held focus: the top bar's Inky
 * toggle when visible, else the main area.
 * @param {HTMLElement|null} main - Main scroller.
 * @param {HTMLElement|null} frame - Dock frame (skipped when searching for the toggle).
 * @returns {void}
 */
export function returnFocusFromDock(main, frame) {
  const toggles = Array.from(document.querySelectorAll('button[aria-controls="inky-dock"]'));
  const toggle = toggles.find((el) => !(frame && frame.contains(el)) && !el.closest('[inert]') && el.getClientRects().length > 0);
  if (toggle) {
    toggle.focus({ preventScroll: true });
    return;
  }
  main?.focus({ preventScroll: true });
}

/**
 * App shell.
 * @param {object} props - Props.
 * @param {(state: {collapsed: boolean, inDrawer: boolean}) => React.ReactNode} props.renderSidebar - Sidebar content.
 * @param {React.ReactNode} props.topbar - Top bar above the page.
 * @param {React.ReactNode} props.dock - Inky dock content (rendered once, always mounted).
 * @param {React.ReactNode} props.children - Routed page.
 * @returns {JSX.Element} Shell.
 */
export default function WorkspaceShell({ renderSidebar, topbar, dock, children }) {
  const layout = useWorkspaceLayout();
  const { dock: dockState, setDock, toggleDock, focusMode, setFocusMode, sidebarCollapsed, mobileNavOpen, setMobileNavOpen, screen, editorChrome } = layout;
  const { pathname, hash } = useLocation();
  const viewport = useViewport();
  const workRef = useRef(null);
  const mainRef = useRef(null);
  const mainStore = useMemo(createMainStore, []);

  const { variant, side, open } = dockState;
  const isPush = variant === 'push';
  const isFloating = variant === 'floating';
  const isOverlay = variant === 'overlay';
  // Editor pages bring their own header (back, menu button), so the top bar and the in-flow
  // sidebar step aside there; the sidebar opens as a drawer from the page's menu button.
  const showSidebar = !focusMode && screen.isMdUp && !editorChrome;
  const showDrawerNav = !focusMode && (!screen.isMdUp || editorChrome);
  const showTopbar = !focusMode && !editorChrome;
  const limits = getDockSizeLimits(side === 'floating' ? 'right' : side, viewport);
  const dockSize = clampNumber(dockState.size ?? limits.defaultSize, limits.min, limits.max, limits.defaultSize);
  const storedRect = dockState.floatingRect;
  const floatingRect = useMemo(() => storedRect || defaultFloatingRect(viewport), [storedRect, viewport]);

  const closeDock = useCallback(() => toggleDock(false), [toggleDock]);
  const commitSize = useCallback((size) => setDock({ size }), [setDock]);
  const commitRect = useCallback((rect) => setDock({ floatingRect: rect }), [setDock]);
  const snapTo = useCallback((nextSide) => setDock({ side: nextSide, open: true }), [setDock]);

  const floating = useFloatingWindow({ rect: floatingRect, enabled: isFloating, onCommit: commitRect, onSnap: snapTo });

  // Main element + width for useMainWidthAtLeast, and --q-main-height for pages that fill it.
  useLayoutEffect(() => {
    const main = mainRef.current;
    if (!main) return undefined;
    mainStore.setElement(main);
    const root = document.documentElement;
    const update = () => {
      const rect = main.getBoundingClientRect();
      mainStore.setWidth(Math.round(rect.width));
      root.style.setProperty('--q-main-height', `${Math.round(rect.height)}px`);
    };
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => {
        window.removeEventListener('resize', update);
        root.style.removeProperty('--q-main-height');
        mainStore.setElement(null);
      };
    }
    const observer = new ResizeObserver(update);
    observer.observe(main);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--q-main-height');
      mainStore.setElement(null);
    };
  }, [mainStore]);

  // New page: scroll main to the top (or to #anchor), close the mobile nav, leave focus mode.
  useLayoutEffect(() => {
    const main = mainRef.current;
    if (!main) return undefined;
    if (hash && hash.length > 1) {
      let id = hash.slice(1);
      try {
        id = decodeURIComponent(id);
      } catch {
        /* keep the raw hash */
      }
      return scrollToAnchorInMain(main, id);
    }
    main.scrollTop = 0;
    return undefined;
  }, [pathname, hash]);
  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname, setMobileNavOpen]);
  const firstPathRef = useRef(pathname);
  useEffect(() => {
    if (firstPathRef.current === pathname) return;
    firstPathRef.current = pathname;
    setFocusMode(false);
  }, [pathname, setFocusMode]);

  // Closing the dock makes it inert. If it held keyboard focus (its own Close button, Esc on the
  // overlay, Mod+J from inside the composer), hand focus back to the Inky toggle instead of
  // letting it fall to <body>.
  const dockHadFocusRef = useRef(false);
  const handleDockFocus = useCallback(() => {
    dockHadFocusRef.current = true;
  }, []);
  const handleDockBlur = useCallback((event) => {
    const frame = floating.frameRef.current;
    if (event.relatedTarget && !(frame && frame.contains(event.relatedTarget))) dockHadFocusRef.current = false;
  }, [floating.frameRef]);
  const wasOpenRef = useRef(open);
  useLayoutEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = open;
    if (!wasOpen || open) return;
    const frame = floating.frameRef.current;
    const active = document.activeElement;
    const focusInDock = Boolean(frame && active && frame.contains(active));
    const focusLost = !active || active === document.body;
    if (focusInDock || (focusLost && dockHadFocusRef.current)) returnFocusFromDock(mainRef.current, frame);
    dockHadFocusRef.current = false;
  }, [open, floating.frameRef]);

  const dockFrame = useMemo(
    () => ({ variant, side, open, dragHandleProps: floating.dragHandleProps, close: closeDock }),
    [variant, side, open, closeDock, floating.dragHandleProps]
  );

  const dockOrder = side === 'left' ? 1 : 3;
  const centerOrder = side === 'left' ? 3 : 1;
  const columnLayout = isPush && side === 'bottom';
  const overlayVisible = isOverlay && open;

  /** Dock frame styles for the current presentation; the element itself never changes. */
  const dockSx = (() => {
    const base = {
      order: dockOrder,
      flex: 'none',
      position: 'relative',
      minWidth: 0,
      minHeight: 0,
      bgcolor: 'var(--q-surface, #FFFFFF)',
      zIndex: 5,
    };
    if (isPush && side !== 'bottom') {
      return {
        ...base,
        width: open ? 'var(--q-dock-size)' : '0px',
        overflow: 'hidden',
        borderLeft: side === 'right' && open ? '1px solid var(--q-border, #E3E0DA)' : 'none',
        borderRight: side === 'left' && open ? '1px solid var(--q-border, #E3E0DA)' : 'none',
        transition: `width ${DOCK_TRANSITION_MS}ms ${EASE}`,
        '& > [data-dock-inner]': { width: 'var(--q-dock-size)', height: '100%' },
      };
    }
    if (isPush) {
      return {
        ...base,
        width: '100%',
        height: open ? 'var(--q-dock-size)' : '0px',
        overflow: 'hidden',
        borderTop: open ? '1px solid var(--q-border, #E3E0DA)' : 'none',
        transition: `height ${DOCK_TRANSITION_MS}ms ${EASE}`,
        '& > [data-dock-inner]': { height: 'var(--q-dock-size)', width: '100%' },
      };
    }
    if (isFloating) {
      return {
        ...base,
        position: 'fixed',
        zIndex: 1250,
        borderRadius: 'var(--q-radius-xl, 12px)',
        border: '1px solid var(--q-border, #E3E0DA)',
        boxShadow: 'var(--q-shadow-xl, 0 24px 48px rgba(0,0,0,0.16))',
        visibility: open ? 'visible' : 'hidden',
        opacity: open ? 1 : 0,
        transform: open ? 'none' : 'translateY(8px) scale(0.98)',
        transition: floating.interacting
          ? 'none'
          : `opacity 160ms ${EASE}, transform 160ms ${EASE}, visibility 0s linear ${open ? '0s' : '160ms'}`,
        '& > [data-dock-inner]': { width: '100%', height: '100%', overflow: 'hidden', borderRadius: 'inherit' },
      };
    }
    return {
      ...base,
      position: 'fixed',
      zIndex: 1250,
      top: 0,
      right: 0,
      bottom: 0,
      width: 'min(100vw, 440px)',
      borderLeft: '1px solid var(--q-border, #E3E0DA)',
      boxShadow: open ? 'var(--q-shadow-xl, 0 24px 48px rgba(0,0,0,0.16))' : 'none',
      transform: open ? 'none' : 'translateX(100%)',
      visibility: open ? 'visible' : 'hidden',
      transition: `transform ${DOCK_TRANSITION_MS}ms ${EASE}, visibility 0s linear ${open ? '0s' : `${DOCK_TRANSITION_MS}ms`}`,
      '& > [data-dock-inner]': { width: '100%', height: '100%' },
    };
  })();

  return (
    <MainStoreContext.Provider value={mainStore}>
      <Box
        data-q-shell=""
        data-focus-mode={focusMode ? 'true' : undefined}
        sx={{ display: 'flex', height: '100vh', '@supports (height: 100dvh)': { height: '100dvh' }, overflow: 'hidden', bgcolor: 'background.default', color: 'text.primary' }}
      >
        <Box
          component="a"
          href="#main-content"
          onClick={(event) => {
            event.preventDefault();
            mainRef.current?.focus();
          }}
          sx={{
            position: 'fixed',
            left: 12,
            top: 12,
            zIndex: 2000,
            px: 1.5,
            py: 1,
            borderRadius: 'var(--q-radius-md, 8px)',
            bgcolor: 'var(--q-surface, #FFFFFF)',
            color: 'var(--q-accent-text, #3A28B8)',
            boxShadow: 'var(--q-shadow-md)',
            transform: 'translateY(-200%)',
            '&:focus': { transform: 'none' },
          }}
        >
          Skip to content
        </Box>

        {showSidebar && (
          <Box
            component="aside"
            aria-label="Sidebar"
            sx={{
              flex: 'none',
              width: sidebarCollapsed ? SIDEBAR_RAIL_WIDTH : SIDEBAR_WIDTH,
              transition: `width 200ms ${EASE}`,
              overflow: 'hidden',
              borderRight: '1px solid var(--q-border, #E3E0DA)',
              bgcolor: 'var(--q-bg, #F6F5F2)',
            }}
          >
            {renderSidebar({ collapsed: sidebarCollapsed, inDrawer: false })}
          </Box>
        )}
        {showDrawerNav && (
          <Drawer
            anchor="left"
            open={mobileNavOpen}
            onClose={() => setMobileNavOpen(false)}
            PaperProps={{ sx: { width: 'min(86vw, 300px)', bgcolor: 'var(--q-bg, #F6F5F2)' } }}
          >
            {renderSidebar({ collapsed: false, inDrawer: true })}
          </Drawer>
        )}

        <Box
          ref={workRef}
          style={{ '--q-dock-size': `${dockSize}px` }}
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: columnLayout ? 'column' : 'row',
            '&[data-resizing="true"] > [data-dock-frame]': { transition: 'none' },
          }}
        >
          <Box key="center" sx={{ order: centerOrder, flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {showTopbar && topbar}
            <Box
              component="main"
              id="main-content"
              ref={mainRef}
              tabIndex={-1}
              data-q-main=""
              inert={overlayVisible ? '' : undefined}
              aria-hidden={overlayVisible ? 'true' : undefined}
              sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                overflow: 'auto',
                overscrollBehavior: 'contain',
                position: 'relative',
                outline: 'none',
                bgcolor: 'background.default',
                '--q-page-sticky-top': '0px',
                '--q-editor-sticky-top': '0px',
              }}
            >
              {children}
            </Box>
          </Box>

          {isPush && open && (
            <SplitHandle
              key="splitter"
              axis={side === 'bottom' ? 'y' : 'x'}
              sign={side === 'left' ? 1 : -1}
              size={dockSize}
              min={limits.min}
              max={limits.max}
              defaultSize={limits.defaultSize}
              onCommit={commitSize}
              targetRef={workRef}
              cssVar="--q-dock-size"
              label="Resize Inky panel"
              controls="inky-dock"
              sx={{ order: 2 }}
            />
          )}

          <Box
            key="dock"
            id="inky-dock"
            component="aside"
            aria-label="Inky"
            data-dock-frame=""
            data-dock-variant={variant}
            data-dock-side={side}
            ref={floating.frameRef}
            inert={open ? undefined : ''}
            aria-hidden={open ? undefined : 'true'}
            onFocus={handleDockFocus}
            onBlur={handleDockBlur}
            sx={dockSx}
          >
            <Box data-dock-inner="">
              <DockFrameContext.Provider value={dockFrame}>{dock}</DockFrameContext.Provider>
            </Box>
            {floating.resizeHandles}
          </Box>
        </Box>

        {isOverlay && (
          <Box
            aria-hidden="true"
            onClick={closeDock}
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 1249,
              bgcolor: 'var(--q-overlay, rgba(28,27,25,0.4))',
              opacity: open ? 1 : 0,
              pointerEvents: open ? 'auto' : 'none',
              transition: `opacity ${DOCK_TRANSITION_MS}ms ${EASE}`,
            }}
          />
        )}
        {floating.snapPreview}

        {focusMode && (
          <Tooltip title="Exit focus mode (Esc)">
            <Button
              onClick={() => setFocusMode(false)}
              startIcon={<FullscreenExit />}
              size="small"
              sx={{
                position: 'fixed',
                top: 12,
                right: isPush && open && side === 'right' ? `calc(var(--q-dock-size, 0px) + 16px)` : 16,
                zIndex: 1260,
                borderRadius: 'var(--q-radius-pill, 999px)',
                bgcolor: 'var(--q-surface, #FFFFFF)',
                color: 'var(--q-text-2, #3D3A35)',
                border: '1px solid var(--q-border, #E3E0DA)',
                boxShadow: 'var(--q-shadow-sm)',
                px: 1.5,
                '&:hover': { bgcolor: 'var(--q-hover, #ECEAE5)' },
              }}
              style={{ '--q-dock-size': `${dockSize}px` }}
            >
              Exit focus
            </Button>
          </Tooltip>
        )}
      </Box>
    </MainStoreContext.Provider>
  );
}
